/**
 * Fetch client for the RecoveryAssure API.
 *
 * Contract:
 *   - `credentials: 'include'` on every call so `ra_session` cookie ships.
 *   - 401 responses call the installed unauthenticated handler (see
 *     `setUnauthorizedHandler`) so the SPA can redirect to /login.
 *   - Non-2xx responses reject with `ApiError(code, message, details, status)`.
 *   - 204 returns `null` (no body).
 *   - `fetch` binary responses (like XLSX downloads) via `binary=true`.
 */
const BASE = process.env.REACT_APP_BACKEND_URL;
const API_ROOT = `${BASE}/api`;

let unauthorizedHandler = null;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

export class ApiError extends Error {
  constructor(code, message, details, status) {
    super(message || code || "Request failed");
    this.name = "ApiError";
    this.code = code || "REQUEST_FAILED";
    this.details = details || {};
    this.status = status;
  }
}

async function parseError(res) {
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  const err = payload && payload.error ? payload.error : {};
  return new ApiError(
    err.code || `HTTP_${res.status}`,
    err.message || res.statusText,
    err.details || {},
    res.status,
  );
}

export async function apiFetch(
  path,
  { method = "GET", body, headers, binary = false, formData = false } = {},
) {
  const url = path.startsWith("http") ? path : `${API_ROOT}${path}`;
  const init = {
    method,
    credentials: "include",
    headers: { ...(headers || {}) },
  };
  if (formData) {
    init.body = body;
  } else if (body !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(url, init);
  } catch (netErr) {
    throw new ApiError("NETWORK_ERROR", netErr.message || "Network error", {}, 0);
  }

  if (res.status === 401) {
    if (unauthorizedHandler) unauthorizedHandler();
    throw await parseError(res);
  }
  if (!res.ok) {
    throw await parseError(res);
  }
  if (res.status === 204) return null;
  if (binary) return res.blob();
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const API_BASE = API_ROOT;
